import { getScopedDomain } from '../lib/tenantScope';
// Event Log — unified view over audit_log_v2 + activity_log, shaped to match
// 3CX (Severity | Event ID | Date | Source | Department | Details).
// Toolbar has Purge + Download. Filter chip expands to inline severity
// checkboxes + Search button (3CX shape — applies on Search, not on toggle).

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trash2, Download, Filter, Search, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { getToken } from '../lib/api';

const auth = () => ({ Authorization: `Bearer ${getToken()}` });

type Row = {
  id: string;
  severity: 'info' | 'warn' | 'error';
  occurred_at: string;
  source: string;
  department: string;
  details: string;
  event_id_int: number;
};

const ALL_SEVS = ['Info', 'Warning', 'Error'] as const;
type SevLabel = typeof ALL_SEVS[number];
const SEV_TO_API: Record<SevLabel, string> = { Info: 'info', Warning: 'warn', Error: 'error' };

export default function EventLog() {
  // Applied filter state — what's actually in the query.
  const [sevs, setSevs]     = useState<SevLabel[]>(['Info', 'Warning', 'Error']);
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterOpen, setFilterOpen] = useState(false);
  const [purgeOpen, setPurgeOpen]   = useState(false);

  // Pending filter state — what's in the expansion area, only committed when
  // the Search button is clicked (matches 3CX UX).
  const [pendingSevs, setPendingSevs]   = useState<SevLabel[]>(['Info', 'Warning', 'Error']);
  const [pendingSearch, setPendingSearch] = useState('');

  // Reset to page 1 whenever applied filters change
  useEffect(() => { setPage(1); }, [sevs, search, pageSize]);

  const offset = (page - 1) * pageSize;
  const sevParam = sevs.map(s => SEV_TO_API[s]).join(',');

  const url = useMemo(() => {
    const p = new URLSearchParams();
    if (sevParam) p.set('severity', sevParam);
    if (search)   p.set('search', search);
    p.set('limit',  String(pageSize));
    p.set('offset', String(offset));
    const d = getScopedDomain(); if (d) p.set('domain_uuid', d);   // department filter
    return `/api/event-log?${p.toString()}`;
  }, [sevParam, search, pageSize, offset]);

  const { data, isLoading } = useQuery<{ rows: Row[]; total: number }>({
    queryKey: ['event-log', sevParam, search, pageSize, offset],
    queryFn: async () => {
      const r = await fetch(url, { headers: auth() });
      if (!r.ok) throw new Error(await r.text());
      return r.json();
    },
  });

  const rows  = data?.rows  || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  function downloadCsv() {
    const p = new URLSearchParams();
    if (sevParam) p.set('severity', sevParam);
    if (search)   p.set('search', search);
    const d = getScopedDomain(); if (d) p.set('domain_uuid', d);   // department filter
    fetch(`/api/event-log/export.csv?${p.toString()}`, { headers: auth() })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `event-log-${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }

  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-semibold">Event Log</h1>

      {/* Toolbar — Purge + Download (3CX-shape primary buttons) */}
      <div className="card p-2 flex flex-wrap gap-1 items-center">
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => setPurgeOpen(true)}>
          <Trash2 size={14} /> Purge
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={downloadCsv}>
          <Download size={14} /> Download
        </button>
      </div>

      {/* Filter row — severity chip (click to expand) + search input */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          className="bg-primary text-white text-sm rounded-md px-3 py-1.5 inline-flex items-center gap-2 hover:bg-primary/90"
          onClick={() => {
            setPendingSevs(sevs);
            setPendingSearch(search);
            setFilterOpen(o => !o);
          }}
          title="Filter severity"
        >
          Severity: {sevs.length === ALL_SEVS.length ? ALL_SEVS.join(', ') : (sevs.length ? sevs.join(', ') : 'None')}
          <Filter size={12} />
        </button>
        <div className="flex-1 min-w-48 flex items-center gap-1 bg-elevated rounded-md px-2 py-1.5">
          <Search size={14} className="text-textSecondary" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search"
            className="bg-transparent outline-none text-sm w-full"
          />
        </div>
      </div>

      {/* Filter expansion — inline severity checkboxes + Search button. Mirrors
          3CX's expanded chip: applies pending state only when Search is clicked. */}
      {filterOpen && (
        <div className="card p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-6">
            <div className="text-sm font-medium w-20">Severity</div>
            {ALL_SEVS.map(s => (
              <label key={s} className="inline-flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={pendingSevs.includes(s)}
                  onChange={(e) => setPendingSevs(prev =>
                    e.target.checked ? [...prev, s] : prev.filter(x => x !== s)
                  )}
                />
                {s}
              </label>
            ))}
          </div>
          <div>
            <button
              className="btn-primary text-sm inline-flex items-center gap-1"
              onClick={() => {
                setSevs(pendingSevs);
                setSearch(pendingSearch);
                setFilterOpen(false);
              }}
            >
              <Search size={14} /> Search
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card p-0 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="w-32">Severity</th>
              <th className="w-24">Event ID</th>
              <th className="w-40">Date</th>
              <th className="w-32">Source</th>
              <th className="w-40">Department</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => <EventRow key={r.id} r={r} />)}
            {!rows.length && !isLoading && (
              <tr><td colSpan={6} className="text-center py-8 text-textSecondary">No events.</td></tr>
            )}
            {isLoading && (
              <tr><td colSpan={6} className="text-center py-8 text-textSecondary">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} pages={pages} pageSize={pageSize}
                  onPage={setPage} onPageSize={setPageSize} />

      {purgeOpen && <PurgeModal onClose={() => setPurgeOpen(false)} />}
    </div>
  );
}

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  // 3CX format: MM/DD/YYYY HH:MM AM/PM
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${String(h).padStart(2,'0')}:${min} ${ap}`;
}

function EventRow({ r }: { r: Row }) {
  const Icon = r.severity === 'error' ? AlertCircle : r.severity === 'warn' ? AlertTriangle : Info;
  const color = r.severity === 'error' ? 'text-danger' : r.severity === 'warn' ? 'text-warn' : 'text-primary';
  const label = r.severity === 'warn' ? 'Warning' : r.severity === 'error' ? 'Error' : 'Info';
  return (
    <tr className="hover:bg-elevated">
      <td>
        <span className="inline-flex items-center gap-1.5 text-sm">
          <Icon size={14} className={color} /> {label}
        </span>
      </td>
      <td className="text-sm text-primary underline-offset-2 hover:underline cursor-pointer">{r.event_id_int}</td>
      <td className="text-sm">{formatStamp(r.occurred_at)}</td>
      <td className="text-sm">{r.source}</td>
      <td className="text-sm">{r.department || ''}</td>
      <td className="text-sm">{r.details}</td>
    </tr>
  );
}

function Pagination({
  page, pages, pageSize, onPage, onPageSize,
}: {
  page: number; pages: number; pageSize: number;
  onPage: (n: number) => void; onPageSize: (n: number) => void;
}) {
  function pageNumbers(): (number | '…')[] {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const out: (number | '…')[] = [1];
    const start = Math.max(2, page - 1);
    const end   = Math.min(pages - 1, page + 1);
    if (start > 2) out.push('…');
    for (let i = start; i <= end; i++) out.push(i);
    if (end < pages - 1) out.push('…');
    out.push(pages);
    return out;
  }
  // 3CX-style chunky page boxes (rounded squares, primary-on-active).
  const boxBase = 'px-3 py-1.5 rounded-md text-sm border border-border bg-card hover:bg-elevated';
  return (
    <div className="flex items-center justify-center gap-1 pt-1">
      <button className={`${boxBase} disabled:opacity-40`}
              disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      {pageNumbers().map((n, i) =>
        n === '…' ? (
          <span key={i} className="px-2 text-textSecondary">…</span>
        ) : (
          <button key={i}
                  className={n === page
                    ? 'px-3 py-1.5 rounded-md text-sm bg-primary text-white border border-primary'
                    : boxBase}
                  onClick={() => onPage(n)}>
            {n}
          </button>
        )
      )}
      <button className={`${boxBase} disabled:opacity-40`}
              disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
      <select className="input w-16 ml-2 text-sm" value={pageSize} onChange={e => onPageSize(Number(e.target.value))}>
        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
      </select>
    </div>
  );
}

function PurgeModal({ onClose }: { onClose: () => void }) {
  const [from, setFrom] = useState('');
  const [to,   setTo]   = useState('');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  async function go() {
    if (!from || !to) { setErr('Pick both dates'); return; }
    if (!confirm(`Permanently delete event log entries from ${from} to ${to}? This cannot be undone.`)) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch('/api/event-log/purge', {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ from, to }),
      });
      if (!r.ok) throw new Error(await r.text());
      onClose();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">Purge events</h2>
        <p className="text-sm text-textSecondary mb-3">
          Permanently delete audit + activity entries in the selected range. This cannot be undone.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="block text-xs text-textSecondary mb-1">From</label>
            <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-textSecondary mb-1">To</label>
            <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
        </div>
        {err && <div className="text-sm text-danger mb-2">{err}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-danger" disabled={busy} onClick={go}>{busy ? 'Purging…' : 'Purge'}</button>
        </div>
      </div>
    </div>
  );
}
